import { QuestionVisibility, type HeldSpaceSummary } from '@models/api/question';

type HeldList = readonly HeldSpaceSummary[] | null | undefined;

/** Held links still waiting on a host. */
export const pendingHeldSpaces = (held: HeldList): HeldSpaceSummary[] =>
  (held ?? []).filter(space => space.status === 'held');

/**
 * True for the two audiences that reach nobody outside the post's Spaces: SPACE_ONLY and
 * SPACE_WIDE. Such a post with no live Space and no live event is live nowhere. The one
 * spelling of the rule on the client; the backend's is `_SCOPED_VISIBILITIES`
 * (app/api/services/held_posts.py).
 */
export const isSpaceScopedVisibility = (visibility: QuestionVisibility): boolean =>
  visibility === QuestionVisibility.space_only || visibility === QuestionVisibility.space_wide;

/** What the held-post rules read off a post, in any of its shapes (API answer or cache entity). */
interface HeldPostInput {
  visibility: QuestionVisibility;
  space_summaries: readonly { id: string | null }[];
  event_summaries: readonly { id: string | null }[];
  held_spaces?: HeldList;
}

/**
 * Space-scoped, in no live Space and in no live event: nobody but the member who posted it can
 * open the post. Only a summary with an id counts as live.
 */
const isLiveNowhere = (question: HeldPostInput): boolean =>
  isSpaceScopedVisibility(question.visibility) &&
  !question.space_summaries.some(space => Boolean(space.id)) &&
  !question.event_summaries.some(event => Boolean(event.id));

export type HeldPostOutcome =
  | { kind: 'live' }
  | { kind: 'held_only'; held: HeldSpaceSummary[] }
  | { kind: 'mixed'; held: HeldSpaceSummary[] };

/**
 * What a create or an edit left waiting, from the server's answer:
 * - live: nothing is still held.
 * - held_only: a Space-scoped post (SPACE_ONLY / SPACE_WIDE) in no live Space and no live
 *   event. Nobody but the member who posted it can see it until a host approves.
 * - mixed: something is held, but the post is already live somewhere (a live Space, a live
 *   event, or an ANYONE / MY_NETWORK audience).
 *
 * The held_only test mirrors a pair in the backend's app/api/services/held_posts.py:
 * `deny_held_only_to_non_author` applies the visibility term (`_SCOPED_VISIBILITIES`) and then
 * asks `is_held_only`, which is the "no live Space link and no event link" term. Here both are
 * read from the summaries the answer carries, with one difference on purpose: this counts only
 * links still waiting on a host, so a post whose every request was declined classifies as live
 * here (there is nothing to wait for). `isHeldOnlyPost` below is the one that counts declined
 * links.
 */
export const classifyHeldPostOutcome = (question: HeldPostInput): HeldPostOutcome => {
  const held = pendingHeldSpaces(question.held_spaces);
  if (held.length === 0) return { kind: 'live' };
  return isLiveNowhere(question) ? { kind: 'held_only', held } : { kind: 'mixed', held };
};

/** The chosen Events of a save, by name: linked to the post now, or waiting with a held Space. */
export interface CrosspostOutcome {
  linked: string[];
  waiting: string[];
}

/**
 * What a save that added Events to a post did with each of them, from the server's answer.
 *
 * An Event is linked now when a live Space of the post hosts it, or when none of the post's
 * Spaces hosts it at all. While every Space of the post that hosts it is held (the author has
 * only asked to join there), the Event waits with those Spaces and is published when a host
 * approves the first of them. The save answers 200 either way. The answer's `event_summaries`
 * lists the post's live Event links only, so a chosen Event that is not there is waiting. The
 * backend's rule is `_place_events` (app/api/services/held_posts.py).
 *
 * `event_summaries` can be absent: a backend without held posts answers this save with what was
 * sent (`QuestionUpdate`), not with the post. Nothing waits on such a backend, so its 200 means
 * what it always meant, that every chosen Event is linked. The dev app can meet one: the webapp
 * reaches dev before the backend does. The composer's edit path guards the same body
 * (`postInAnswer`, useUpdateQuestionMutation.ts).
 */
export const classifyCrosspostOutcome = (
  chosen: readonly { id: string; name: string }[],
  answer: { event_summaries?: readonly { id: string | null }[] },
): CrosspostOutcome => {
  const summaries = answer.event_summaries;
  if (!summaries) return { linked: chosen.map(event => event.name), waiting: [] };
  const linkedIds = new Set(summaries.map(event => event.id));
  return {
    linked: chosen.filter(event => linkedIds.has(event.id)).map(event => event.name),
    waiting: chosen.filter(event => !linkedIds.has(event.id)).map(event => event.name),
  };
};

/**
 * True when the post is live nowhere: Space-scoped (SPACE_ONLY / SPACE_WIDE), in no live Space
 * and no live event, with at least one held Space link in ANY status. Nobody but its author can
 * open such a post, so its link is not worth sending to anyone.
 *
 * Mirrors the backend's `deny_held_only_to_non_author` (app/api/services/held_posts.py), which
 * answers 404 to everyone else for such posts: it applies the visibility term and then asks
 * `is_held_only`, which counts declined links too. A post whose only request was declined is
 * still live nowhere. That is why this is not built on `classifyHeldPostOutcome` or
 * `pendingHeldSpaces`, which drop declined links.
 *
 * A mirror, not the same test: the backend counts the post's link rows, and this reads the
 * summaries the viewer was sent, where only an entry with an id counts as live. The two can
 * differ at the edges (a live link to a Space the viewer is not shown, for one), and the
 * backend's answer is the one that decides who can open the post.
 *
 * The held link is required on purpose. A Space-scoped post with no visible Space and no held
 * link is the older case of a Space hidden from its author (they left an unlisted Space): its
 * members can still open the link, so it keeps its Share.
 */
export const isHeldOnlyPost = (question: HeldPostInput): boolean =>
  (question.held_spaces ?? []).length > 0 && isLiveNowhere(question);
